import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';

/**
 * The header's agent light ("TinyCMS UX optimization review v6", Prototype v6):
 * green "Agent working" while the site's agent is using its tools (a call in
 * the last few minutes); a quiet grey dot, with when it was last active, once
 * it has been used; nothing for a site whose agent was never connected.
 */

const AGENT_ACTIVITY = gql`
  query AgentActivity($accountId: ID!) {
    agentActivity(accountId: $accountId) {
      lastCallAt
      callsLastHour
    }
  }
`;

const WORKING_MS = 5 * 60 * 1000;

const ago = (ms: number) => {
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
};

export function AgentStatus() {
  const { accountId } = useAccount();
  const { data } = useQuery<{
    agentActivity: { lastCallAt?: string | null; callsLastHour: number };
  }>(AGENT_ACTIVITY, {
    variables: { accountId },
    skip: !accountId,
    pollInterval: 60000,
    fetchPolicy: 'cache-and-network',
  });
  const last = data?.agentActivity.lastCallAt ? new Date(data.agentActivity.lastCallAt) : null;
  if (!last || Number.isNaN(last.getTime())) return null;
  const since = Date.now() - last.getTime();
  const working = since < WORKING_MS;
  const label = working ? 'Agent working' : `Agent last active ${ago(since)}`;
  return (
    <span
      className="ink-agent"
      data-working={working ? 'true' : 'false'}
      title={label}
      role="status"
      aria-label={label}
    >
      <span className="ink-agent-dot" />
      {working ? <span className="ink-agent-label">Agent working</span> : null}
    </span>
  );
}
